import AxiosService from '@core/services/app/axiosService';
import authHeader from '@core/services/app/authHeader';
import appNotificationService from '@core/services/utils/appNotificationService';
import appConsoleService from '@core/services/utils/appConsoleService';
import {useAppPushStore} from '@core/stores/app/appPushStore';

/**
 * AppPushService — Web Push app-wide (core/app).
 * En mode PWA, le service worker est enregistré par Quasar (sw.js, cf.
 * src-pwa/custom-service-worker.js) ; ce service pilote l'abonnement PushManager
 * (VAPID) et la synchronisation avec le backend (/appPush/*). Singleton.
 */
class AppPushService {

  getStore() {
    if (!this.store) {
      this.store = useAppPushStore()
    }
    return this.store
  }

  isSupported() {
    return typeof window !== 'undefined'
      && 'serviceWorker' in navigator
      && 'PushManager' in window
      && 'Notification' in window;
  }

  /** Récupère la registration du SW Quasar et synchronise l'état. Appelé au boot. */
  async register() {
    if (!this.isSupported()) return null;
    try {
      await this.getRegistration();
      await this.syncState();
      return this._registration;
    } catch (error) {
      appConsoleService.error('appPushService', error);
      return null;
    }
  }

  /**
   * Registration du service worker enregistré par Quasar.
   * waitReady=true attend qu'un SW soit actif (utile avant subscribe) ;
   * sinon retour immédiat (peut être null tant que le SW n'est pas prêt).
   */
  async getRegistration({waitReady = false} = {}) {
    if (this._registration) return this._registration;
    if (!this.isSupported()) return null;
    let reg = await navigator.serviceWorker.getRegistration();
    if (!reg && waitReady) {
      // `ready` ne se résout jamais sans SW (ex: build SPA) → timeout pour échouer proprement.
      reg = await Promise.race([
        navigator.serviceWorker.ready,
        new Promise((resolve) => setTimeout(() => resolve(null), 5000)),
      ]);
    }
    this._registration = reg || null;
    return this._registration;
  }

  /** Met à jour le store (supporté / permission / abonné). */
  async syncState() {
    const store = this.getStore();
    store.setSupported(this.isSupported());
    if (!this.isSupported()) return;
    store.setPermission(Notification.permission);
    try {
      const reg = await this.getRegistration();
      const sub = reg ? await reg.pushManager.getSubscription() : null;
      store.setSubscribed(!!sub);
    } catch (error) {
      store.setSubscribed(false);
    }
  }

  /** Active les notifications : permission → souscription → enregistrement backend. */
  async enable() {
    if (!this.isSupported()) {
      appNotificationService.pushWarning("Ce navigateur ne supporte pas les notifications.");
      return false;
    }
    const store = this.getStore();
    store.setBusy(true);
    try {
      const permission = await Notification.requestPermission();
      store.setPermission(permission);
      if (permission !== 'granted') {
        appNotificationService.pushWarning("Notifications refusées. Tu peux les réactiver dans les réglages du navigateur.");
        return false;
      }

      const response = await AxiosService.get('/appPush/publicKey', {headers: authHeader()});
      const publicKey = response.data.publicKey;

      const reg = await this.getRegistration({waitReady: true});
      if (!reg) {
        appNotificationService.pushError("Service worker indisponible (l'app doit être servie en mode PWA).");
        return false;
      }
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: this._urlBase64ToUint8Array(publicKey),
      });

      await AxiosService.post('/appPush/subscribe', this._serialize(sub), {headers: authHeader()});
      store.setSubscribed(true);
      appNotificationService.pushInfo("Notifications activées sur cet appareil.");
      return true;
    } catch (error) {
      appNotificationService.pushError(error);
      return false;
    } finally {
      store.setBusy(false);
    }
  }

  /** Désactive les notifications sur cet appareil. */
  async disable() {
    const store = this.getStore();
    store.setBusy(true);
    try {
      const reg = await this.getRegistration();
      const sub = reg ? await reg.pushManager.getSubscription() : null;
      if (sub) {
        await AxiosService.post('/appPush/unsubscribe', {endpoint: sub.endpoint}, {headers: authHeader()});
        await sub.unsubscribe();
      }
      store.setSubscribed(false);
      return true;
    } catch (error) {
      appNotificationService.pushError(error);
      return false;
    } finally {
      store.setBusy(false);
    }
  }

  /** Déclenche une notification de test envoyée par le backend (+ feedback). */
  async sendTest() {
    try {
      const res = await AxiosService.post('/appPush/test', {}, {headers: authHeader()});
      const sent = res?.data?.sent ?? 0;
      if (sent > 0) {
        appNotificationService.pushInfo(`Notification de test envoyée (${sent} appareil${sent > 1 ? 's' : ''}).`);
      } else {
        appNotificationService.pushWarning("Aucun appareil abonné. Active d'abord les notifications sur cet appareil.");
      }
      return sent > 0;
    } catch (error) {
      appNotificationService.pushError(error);
      return false;
    }
  }

  // --- internes ---

  _serialize(sub) {
    const json = sub.toJSON();
    return {
      endpoint: sub.endpoint,
      keys: {p256dh: json.keys.p256dh, auth: json.keys.auth},
    };
  }

  _urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const raw = atob(base64);
    const output = new Uint8Array(raw.length);
    for (let i = 0; i < raw.length; ++i) output[i] = raw.charCodeAt(i);
    return output;
  }
}

export default new AppPushService();
